import { useQuery } from "@tanstack/react-query";
import { formatUsdcBaseUnits } from "@hybrid-escrow/shared";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { POLL } from "../lib/polling";
import { StarRatingDisplay } from "../components/StarRating";
import { SellerBadges } from "../components/SellerBadges";
import { ErrorBanner, LoadingState } from "../components/States";
import { usePageMeta } from "../lib/seo";

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="admin-metric-tile">
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

function RevenueChart({ months }: { months: { month: string; revenueBaseUnits: string }[] }) {
  const values = months.map((m) => Number(m.revenueBaseUnits));
  const max = Math.max(1, ...values);
  const width = 720;
  const height = 160;
  const barGap = 6;
  const barWidth = (width - barGap * (months.length - 1)) / months.length;

  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${width} ${height + 24}`} width="100%" style={{ minWidth: 480 }}>
        {months.map((m, i) => {
          const barHeight = (values[i]! / max) * height;
          const x = i * (barWidth + barGap);
          const y = height - barHeight;
          return (
            <g key={m.month}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill="var(--color-primary)"
                rx={2}
              >
                <title>
                  {m.month}: ${formatUsdcBaseUnits(BigInt(m.revenueBaseUnits))}
                </title>
              </rect>
              <text
                x={x + barWidth / 2}
                y={height + 16}
                textAnchor="middle"
                fontSize="9"
                fill="var(--color-text-muted)"
              >
                {m.month.slice(5)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function SellerAnalyticsPage() {
  usePageMeta({
    title: "Sales analytics",
    description: "Your seller performance on MarketOnBase.",
    path: "/sell/analytics",
    index: false,
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.sellerAnalytics,
    queryFn: () => apiClient.getSellerAnalytics(),
    refetchInterval: POLL.catalogue,
  });

  if (isLoading) return <LoadingState label="Loading analytics…" />;
  if (isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  return (
    <div className="stack">
      <h1>Sales analytics</h1>

      <div className="admin-metric-grid">
        <Tile label="Total orders" value={String(data.totalOrders)} />
        <Tile label="Completed sales" value={String(data.completedSales)} />
        <Tile label="Pending sales" value={String(data.pendingSales)} />
        <Tile label="Disputed" value={String(data.disputedOrders)} />
        <Tile label="Refunded" value={String(data.refundedOrders)} />
        <Tile
          label="Total revenue"
          value={`$${formatUsdcBaseUnits(BigInt(data.totalRevenueBaseUnits))}`}
        />
        <Tile
          label="Pending revenue (held in contract)"
          value={`$${formatUsdcBaseUnits(BigInt(data.pendingRevenueBaseUnits))}`}
        />
        <Tile
          label="Average order value"
          value={
            data.averageOrderValueBaseUnits
              ? `$${formatUsdcBaseUnits(BigInt(data.averageOrderValueBaseUnits))}`
              : "—"
          }
        />
      </div>

      <div className="card stack">
        <div className="row row--between">
          <h2>Seller rating</h2>
          <StarRatingDisplay rating={data.sellerRating} />
        </div>
        {data.sellerBadges.length > 0 ? (
          <SellerBadges badges={data.sellerBadges} />
        ) : (
          <p className="text-sm muted">
            No badges yet — Top Rated needs 5+ reviews averaging 4.5★, Trusted Seller needs 10+
            completed sales with a low dispute rate.
          </p>
        )}
      </div>

      <div className="card stack">
        <h2>Revenue, last 12 months</h2>
        <p className="text-sm muted">Completed (RELEASED) sales only.</p>
        <RevenueChart months={data.revenueByMonth} />
      </div>
    </div>
  );
}
